<template>
  <div class="expense-show-container">
    <div
      v-if="loading"
      class="loading-state"
    >
      <i class="fas fa-spinner fa-spin" /> Chargement...
    </div>

    <div v-else-if="expense">
      <!-- Header -->
      <div class="page-header">
        <div>
          <h1>Détails de la Dépense</h1>
          <p class="subtitle">
            {{ expense.reference_number }}
          </p>
        </div>
        <div class="header-actions">
          <router-link
            to="/expenses"
            class="btn btn-secondary"
          >
            <i class="fas fa-arrow-left" /> Retour
          </router-link>
          <router-link
            v-if="expense.status === 'draft'"
            :to="`/expenses/edit/${expense.id}`"
            class="btn btn-primary"
          >
            <i class="fas fa-edit" /> Modifier
          </router-link>
          <button
            v-if="expense.status === 'draft'"
            class="btn btn-success"
            @click="confirmExpense"
          >
            <i class="fas fa-check" /> Confirmer
          </button>
          <button
            v-if="expense.status !== 'cancelled'"
            class="btn btn-danger"
            @click="cancelExpense"
          >
            <i class="fas fa-times" /> Annuler
          </button>
        </div>
      </div>

      <!-- Status Banner -->
      <div
        class="status-banner"
        :class="expense.status"
      >
        <div class="status-content">
          <i
            class="fas"
            :class="getStatusIcon(expense.status)"
          />
          <div>
            <div class="status-label">
              Statut
            </div>
            <div class="status-value">
              {{ expense.status_display }}
            </div>
          </div>
        </div>
        <div
          v-if="expense.status === 'confirmed' && expense.confirmed_by_details"
          class="confirmation-info"
        >
          <div class="info-item">
            <i class="fas fa-user" />
            Confirmé par: {{ expense.confirmed_by_details.username }}
          </div>
          <div class="info-item">
            <i class="fas fa-calendar" />
            Le: {{ formatDateTime(expense.confirmed_at) }}
          </div>
        </div>
      </div>

      <div class="content-grid">
        <!-- Main Information -->
        <div class="info-card">
          <h3 class="card-title">
            Informations Principales
          </h3>

          <div class="info-grid">
            <div class="info-item">
              <label>Type</label>
              <div class="value">
                <span
                  class="type-badge"
                  :class="expense.expense_type.toLowerCase()"
                >
                  <i :class="expense.expense_type === 'IN' ? 'fas fa-arrow-down' : 'fas fa-arrow-up'" />
                  {{ expense.expense_type_display }}
                </span>
              </div>
            </div>

            <div class="info-item">
              <label>Date</label>
              <div class="value">
                {{ formatDate(expense.date) }}
              </div>
            </div>

            <div class="info-item">
              <label>Catégorie</label>
              <div class="value">
                <span
                  v-if="expense.category_name"
                  class="category-badge"
                  :style="{ backgroundColor: expense.category_color }"
                >
                  {{ expense.category_name }}
                </span>
                <span
                  v-else
                  class="text-muted"
                >-</span>
              </div>
            </div>

            <div class="info-item">
              <label>Fournisseur</label>
              <div class="value">
                {{ expense.vendor_name || '-' }}
              </div>
            </div>

            <div class="info-item">
              <label>Livreur</label>
              <div class="value">
                {{ expense.driver_name || '-' }}
              </div>
            </div>

            <div class="info-item">
              <label>Client (Fournisseur)</label>
              <div class="value">
                {{ expense.supplier_name || '-' }}
              </div>
            </div>

            <div
              v-if="expense.depot_details"
              class="info-item"
            >
              <label>Dépôt</label>
              <div class="value">
                <div class="entity-details">
                  <strong>{{ expense.depot_details.name }}</strong>
                  <div
                    v-if="expense.depot_details.monthly_rent_amount"
                    class="entity-detail-line"
                  >
                    <i class="fas fa-money-bill-wave" />
                    Loyer mensuel: {{ formatAmount(expense.depot_details.monthly_rent_amount) }} DT
                  </div>
                  <div
                    v-if="expense.depot_details.address"
                    class="entity-detail-line"
                  >
                    <i class="fas fa-map-marker-alt" />
                    {{ expense.depot_details.address }}
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="expense.car_details"
              class="info-item"
            >
              <label>Voiture</label>
              <div class="value">
                <div class="entity-details">
                  <strong>{{ expense.car_details.serial_number }}</strong>
                  <div class="entity-detail-line">
                    <i class="fas fa-car" />
                    {{ expense.car_details.brand }} {{ expense.car_details.model || '' }} - {{ expense.car_details.color }}
                  </div>
                  <div
                    v-if="expense.car_details.plate_number"
                    class="entity-detail-line"
                  >
                    <i class="fas fa-id-card" />
                    Immatriculation: {{ expense.car_details.plate_number }}
                  </div>
                  <div
                    v-if="expense.car_details.is_rented && expense.car_details.monthly_rental_amount"
                    class="entity-detail-line"
                  >
                    <i class="fas fa-money-bill-wave" />
                    Loyer mensuel: {{ formatAmount(expense.car_details.monthly_rental_amount) }} DT
                  </div>
                </div>
              </div>
            </div>

            <div class="info-item full-width">
              <label>Description</label>
              <div class="value">
                {{ expense.description }}
              </div>
            </div>

            <div
              v-if="expense.notes"
              class="info-item full-width"
            >
              <label>Notes</label>
              <div class="value notes">
                {{ expense.notes }}
              </div>
            </div>
          </div>
        </div>

        <!-- Amount Summary -->
        <div
          class="info-card amount-card"
          :class="expense.expense_type.toLowerCase()"
        >
          <h3 class="card-title">
            Montant
          </h3>

          <div class="amount-display">
            <div class="amount-label">
              {{ expense.expense_type === 'IN' ? 'Revenu' : 'Dépense' }}
            </div>
            <div class="amount-value">
              {{ expense.expense_type === 'IN' ? '+' : '-' }}{{ formatAmount(expense.amount) }} DT
            </div>
          </div>

          <div class="amount-details">
            <div class="detail-row">
              <span>Mode de calcul:</span>
              <span>{{ expense.manual_amount ? 'Manuel' : 'Automatique' }}</span>
            </div>
            <div
              v-if="!expense.manual_amount"
              class="detail-row"
            >
              <span>Nombre d'articles:</span>
              <span>{{ expense.item_count }}</span>
            </div>
            <div
              v-if="!expense.manual_amount && expense.total_from_items"
              class="detail-row"
            >
              <span>Total calculé:</span>
              <span>{{ formatAmount(expense.total_from_items) }} DT</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Items Table -->
      <div
        v-if="expense.items && expense.items.length > 0"
        class="info-card"
      >
        <h3 class="card-title">
          Articles / Lignes de Dépense
        </h3>

        <div class="items-table">
          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th>Quantité</th>
                <th>Prix Unitaire</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in expense.items"
                :key="item.id"
              >
                <td>
                  <div class="item-description">
                    {{ item.description }}
                  </div>
                  <div
                    v-if="item.notes"
                    class="item-notes"
                  >
                    {{ item.notes }}
                  </div>
                </td>
                <td>{{ formatAmount(item.quantity) }}</td>
                <td>{{ formatAmount(item.unit_price) }} DT</td>
                <td class="total-cell">
                  {{ formatAmount(item.total) }} DT
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td
                  colspan="3"
                  class="text-right"
                >
                  <strong>Total:</strong>
                </td>
                <td class="total-cell">
                  <strong>{{ formatAmount(expense.total_from_items) }} DT</strong>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- Attachments -->
      <div
        v-if="expense.attachments && expense.attachments.length > 0"
        class="info-card"
      >
        <h3 class="card-title">
          <i class="fas fa-paperclip" /> Pièces Jointes ({{ expense.attachments.length }})
        </h3>

        <div class="attachments-list">
          <div
            v-for="attachment in expense.attachments"
            :key="attachment.id"
            class="attachment-item"
          >
            <div class="attachment-icon">
              <i
                class="fas"
                :class="getFileIcon(attachment.filename)"
              />
            </div>
            <div class="attachment-info">
              <div class="attachment-name">
                {{ attachment.filename }}
              </div>
              <div class="attachment-date">
                Ajouté le {{ formatDateTime(attachment.created_at) }}
              </div>
            </div>
            <button
              class="btn btn-primary btn-sm"
              @click="downloadAttachment(attachment)"
            >
              <i class="fas fa-download" /> Télécharger
            </button>
          </div>
        </div>
      </div>

      <!-- Audit Trail -->
      <div class="info-card audit-card">
        <h3 class="card-title">
          Historique
        </h3>

        <div class="audit-grid">
          <div class="audit-item">
            <i class="fas fa-plus-circle" />
            <div>
              <div class="audit-label">
                Créé le
              </div>
              <div class="audit-value">
                {{ formatDateTime(expense.created_at) }}
              </div>
            </div>
          </div>

          <div class="audit-item">
            <i class="fas fa-edit" />
            <div>
              <div class="audit-label">
                Dernière modification
              </div>
              <div class="audit-value">
                {{ formatDateTime(expense.updated_at) }}
              </div>
            </div>
          </div>

          <div
            v-if="expense.confirmed_at"
            class="audit-item"
          >
            <i class="fas fa-check-circle" />
            <div>
              <div class="audit-label">
                Confirmé le
              </div>
              <div class="audit-value">
                {{ formatDateTime(expense.confirmed_at) }}
              </div>
            </div>
          </div>

          <div
            v-if="expense.confirmed_by_details"
            class="audit-item"
          >
            <i class="fas fa-user-check" />
            <div>
              <div class="audit-label">
                Confirmé par
              </div>
              <div class="audit-value">
                {{ expense.confirmed_by_details.username }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-else
      class="error-state"
    >
      <i class="fas fa-exclamation-triangle" />
      <p>Dépense introuvable</p>
      <router-link
        to="/expenses"
        class="btn btn-primary"
      >
        Retour à la liste
      </router-link>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ExpenseShow',
  data() {
    return {
      expense: null,
      loading: true,
    }
  },
  mounted() {
    this.loadExpense()
  },
  methods: {
    async loadExpense() {
      this.loading = true
      try {
        const response = await axios.get(`/api/expenses/detail/${this.$route.params.id}/`)
        this.expense = response.data
      } catch (error) {
        console.error('Error loading expense:', error)
        this.$toast.error('Erreur lors du chargement de la dépense')
      } finally {
        this.loading = false
      }
    },

    async confirmExpense() {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Confirmer la dépense ${this.expense.reference_number} ?`)) {
        return
      }

      try {
        await axios.patch(`/api/expenses/confirm/${this.expense.id}/`)
        this.$toast.success('Dépense confirmée avec succès')
        this.loadExpense()
      } catch (error) {
        console.error('Error confirming expense:', error)
        this.$toast.error('Erreur lors de la confirmation')
      }
    },

    async cancelExpense() {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Annuler la dépense ${this.expense.reference_number} ?`)) {
        return
      }

      try {
        await axios.post(`/api/expenses/cancel/${this.expense.id}/`)
        this.$toast.success('Dépense annulée avec succès')
        this.loadExpense()
      } catch (error) {
        console.error('Error cancelling expense:', error)
        this.$toast.error('Erreur lors de l\'annulation')
      }
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('fr-FR')
    },

    formatDateTime(datetime) {
      return new Date(datetime).toLocaleString('fr-FR')
    },

    formatAmount(amount) {
      return parseFloat(amount).toFixed(3)
    },

    getStatusIcon(status) {
      const icons = {
        draft: 'fa-file-alt',
        confirmed: 'fa-check-circle',
        cancelled: 'fa-times-circle',
      }
      return icons[status] || 'fa-file'
    },

    getFileIcon(filename) {
      if (!filename) return 'fa-file'
      const ext = filename.split('.').pop().toLowerCase()
      const iconMap = {
        pdf: 'fa-file-pdf',
        jpg: 'fa-file-image',
        jpeg: 'fa-file-image',
        png: 'fa-file-image',
        gif: 'fa-file-image',
        webp: 'fa-file-image',
        doc: 'fa-file-word',
        docx: 'fa-file-word',
        xls: 'fa-file-excel',
        xlsx: 'fa-file-excel',
      }
      return iconMap[ext] || 'fa-file'
    },

    async downloadAttachment(attachment) {
      try {
        // Fetch file with authentication
        const response = await axios.get(
          `/api/expenses/${this.expense.id}/attachments/${attachment.id}/download/`,
          { responseType: 'blob' },
        )

        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', attachment.filename || 'attachment')
        document.body.appendChild(link)
        link.click()

        // Cleanup
        link.remove()
        window.URL.revokeObjectURL(url)

        this.$toast.success('Fichier téléchargé avec succès')
      } catch (error) {
        console.error('Error downloading attachment:', error)
        this.$toast.error('Erreur lors du téléchargement du fichier')
      }
    },
  },
}
</script>

<style scoped>
.expense-show-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
}

.page-header h1 {
  font-size: 28px;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 5px 0;
}

.subtitle {
  color: #6b7280;
  font-size: 14px;
  margin: 0;
  font-family: 'Courier New', monospace;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.status-banner {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-left: 4px solid #d1d5db;
}

.status-banner.draft {
  border-left-color: #f59e0b;
  background: linear-gradient(to right, #fef3c7 0%, white 10%);
}

.status-banner.confirmed {
  border-left-color: #10b981;
  background: linear-gradient(to right, #d1fae5 0%, white 10%);
}

.status-banner.cancelled {
  border-left-color: #6b7280;
  background: linear-gradient(to right, #f3f4f6 0%, white 10%);
}

.status-content {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
}

.status-content i {
  font-size: 32px;
}

.status-banner.draft .status-content i {
  color: #f59e0b;
}

.status-banner.confirmed .status-content i {
  color: #10b981;
}

.status-banner.cancelled .status-content i {
  color: #6b7280;
}

.status-label {
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-value {
  font-size: 20px;
  font-weight: 600;
  color: #1a202c;
}

.confirmation-info {
  display: flex;
  gap: 30px;
  padding-top: 15px;
  border-top: 1px solid #e5e7eb;
}

.confirmation-info .info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #4b5563;
}

.confirmation-info .info-item i {
  color: #9ca3af;
}

.content-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.info-card {
  background: white;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 20px 0;
  padding-bottom: 15px;
  border-bottom: 2px solid #f3f4f6;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.info-item {
  display: flex;
  flex-direction: column;
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-item label {
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 5px;
}

.info-item .value {
  font-size: 15px;
  color: #1a202c;
  font-weight: 500;
}

.info-item .value.notes {
  white-space: pre-wrap;
  line-height: 1.6;
}

.type-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
}

.type-badge.in {
  background: #d1fae5;
  color: #065f46;
}

.type-badge.out {
  background: #fee2e2;
  color: #991b1b;
}

.category-badge {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  color: white;
}

.amount-card {
  background: linear-gradient(135deg, #f9fafb 0%, white 100%);
}

.amount-card.in {
  background: linear-gradient(135deg, #d1fae5 0%, white 100%);
}

.amount-card.out {
  background: linear-gradient(135deg, #fee2e2 0%, white 100%);
}

.amount-display {
  text-align: center;
  padding: 20px;
  margin-bottom: 20px;
}

.amount-label {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 10px;
}

.amount-value {
  font-size: 36px;
  font-weight: 700;
  font-family: 'Courier New', monospace;
}

.amount-card.in .amount-value {
  color: #059669;
}

.amount-card.out .amount-value {
  color: #dc2626;
}

.amount-details {
  border-top: 1px solid #e5e7eb;
  padding-top: 15px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 14px;
}

.detail-row span:first-child {
  color: #6b7280;
}

.detail-row span:last-child {
  font-weight: 500;
  color: #1a202c;
}

.items-table {
  overflow-x: auto;
}

.items-table table {
  width: 100%;
  border-collapse: collapse;
}

.items-table th {
  text-align: left;
  padding: 12px;
  background: #f9fafb;
  font-size: 13px;
  font-weight: 600;
  color: #4b5563;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e5e7eb;
}

.items-table td {
  padding: 12px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 14px;
}

.item-description {
  font-weight: 500;
  color: #1a202c;
}

.item-notes {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
}

.total-cell {
  font-weight: 600;
  font-family: 'Courier New', monospace;
  color: #1a202c;
}

.items-table tfoot td {
  padding: 15px 12px;
  font-size: 16px;
  background: #f9fafb;
  border-top: 2px solid #e5e7eb;
}

.text-right {
  text-align: right;
}

.attachments-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.attachment-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 10px;
  transition: all 0.2s;
}

.attachment-item:hover {
  background: #f3f4f6;
  transform: translateX(4px);
}

.attachment-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.attachment-icon i {
  font-size: 24px;
  color: #6b7280;
}

.attachment-icon .fa-file-pdf {
  color: #ef4444;
}

.attachment-icon .fa-file-image {
  color: #3b82f6;
}

.attachment-icon .fa-file-word {
  color: #2563eb;
}

.attachment-icon .fa-file-excel {
  color: #059669;
}

.attachment-info {
  flex: 1;
  min-width: 0;
}

.attachment-name {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-date {
  font-size: 12px;
  color: #9ca3af;
}

.btn-sm {
  padding: 8px 16px;
  font-size: 13px;
}

.audit-card {
  background: linear-gradient(135deg, #f9fafb 0%, white 100%);
}

.audit-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.audit-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.audit-item i {
  font-size: 24px;
  color: #9ca3af;
}

.audit-label {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
}

.audit-value {
  font-size: 14px;
  font-weight: 500;
  color: #1a202c;
}

.loading-state,
.error-state {
  padding: 100px 20px;
  text-align: center;
  color: #6b7280;
}

.error-state i {
  font-size: 64px;
  color: #ef4444;
  margin-bottom: 20px;
}

.error-state p {
  font-size: 18px;
  margin-bottom: 20px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-success {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.btn-success:hover {
  background: linear-gradient(135deg, #059669, #047857);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
}

.btn-danger:hover {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.btn-secondary {
  background: #f3f4f6;
  color: #4b5563;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

.text-muted {
  color: #9ca3af;
}

.entity-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.entity-details strong {
  color: #1a202c;
  font-size: 15px;
  margin-bottom: 4px;
}

.entity-detail-line {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #4b5563;
  padding-left: 4px;
}

.entity-detail-line i {
  width: 16px;
  color: #9ca3af;
  font-size: 12px;
}

@media (max-width: 768px) {
  .content-grid {
    grid-template-columns: 1fr;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .header-actions {
    flex-direction: column;
  }
}
</style>
